import {
  array,
  boolean,
  enums,
  Infer,
  number,
  optional,
  record,
  string,
  type,
  union,
} from "superstruct";
import { isStruct } from "../utils/isStruct";
import { CallQuestionAnswerTypeStruct } from "./CallQuestionAnswerType";

/**
 * Question answer in webhook payload.
 */
export const WebhookQuestionAnswerStruct = type({
  questionId: string(),
  question: string(),
  type: CallQuestionAnswerTypeStruct,
  answer: union([string(), number(), boolean()]),
});

export type WebhookQuestionAnswer = Infer<typeof WebhookQuestionAnswerStruct>;

/**
 * Event type for a webhook delivery:
 * - "CALL_ANALYSIS_COMPLETED": the first-ever webhook delivery for this call.
 * - "CALL_ANALYSIS_UPDATED": a subsequent delivery sent because the call's
 *   summary and/or question answers were regenerated after the initial delivery.
 */
export const WEBHOOK_PAYLOAD_EVENT_TYPES = [
  "CALL_ANALYSIS_COMPLETED",
  "CALL_ANALYSIS_UPDATED",
] as const;

export const WebhookPayloadEventTypeStruct = enums(WEBHOOK_PAYLOAD_EVENT_TYPES);

export type WebhookPayloadEventType = Infer<
  typeof WebhookPayloadEventTypeStruct
>;

export function isWebhookPayloadEventType(
  obj: unknown,
): obj is WebhookPayloadEventType {
  return isStruct(
    obj,
    WebhookPayloadEventTypeStruct,
    "WebhookPayloadEventType",
  );
}

/**
 * Webhook payload sent when a call is fully analyzed.
 * Matches the CSV export format as JSON.
 */
export const WebhookPayloadStruct = type({
  /**
   * Event type for this delivery. See `WebhookPayloadEventTypeStruct`.
   */
  eventType: WebhookPayloadEventTypeStruct,
  /**
   * Unique event ID for idempotency.
   */
  eventId: string(),
  /**
   * ISO timestamp when the event was generated.
   */
  eventTimestamp: string(),
  /**
   * Organization ID that owns this call.
   */
  organizationId: string(),
  /**
   * Project ID the call belongs to.
   */
  projectId: string(),
  /**
   * Project name (human-readable).
   */
  projectName: string(),
  /**
   * Internal call ID.
   */
  callId: string(),
  /**
   * External call ID from the phone system.
   */
  externalCallId: optional(string()),
  /**
   * Call date (ISO date string, e.g., "2026-01-28").
   */
  date: string(),
  /**
   * Call start time (ISO timestamp with timezone).
   */
  startTime: string(),
  /**
   * Call duration in seconds.
   */
  durationInSeconds: number(),
  /**
   * Time spent waiting in queue in seconds.
   */
  waitingTimeInSeconds: number(),
  /**
   * Time spent on hold in seconds.
   */
  holdTimeInSeconds: number(),
  /**
   * Pre-work time in seconds.
   */
  preWorkTimeInSeconds: number(),
  /**
   * Post-work (after-call work) time in seconds.
   */
  postWorkTimeInSeconds: number(),
  /**
   * Total time in seconds.
   */
  totalTimeInSeconds: number(),
  /**
   * Whether a voicefile is available.
   */
  isVoicefileAvailable: boolean(),
  /**
   * Whether a transcript is available.
   */
  isTranscriptAvailable: boolean(),
  /**
   * Whether the call has flagged issues.
   */
  hasIssues: boolean(),
  /**
   * Call direction: "INBOUND" or "OUTBOUND".
   */
  direction: optional(string()),
  /**
   * Source phone number.
   */
  sourcePhoneNumber: optional(string()),
  /**
   * Destination phone number.
   */
  destinationPhoneNumber: optional(string()),
  /**
   * Agent name.
   */
  agentName: optional(string()),
  /**
   * Call category name (AI-generated).
   */
  callCategory: optional(string()),
  /**
   * Call summary (AI-generated).
   */
  summary: optional(string()),
  /**
   * Question answers (AI-generated).
   */
  questionAnswers: optional(array(WebhookQuestionAnswerStruct)),
  /**
   * Dynamic question answers as key-value pairs.
   * Key is the question text, value is the answer.
   * Matches CSV export format.
   */
  questionAnswersFlat: optional(
    record(string(), union([string(), number(), boolean()])),
  ),
  /**
   * ISO timestamp when the call was created in the system.
   */
  createdAt: string(),
  /**
   * ISO timestamp when the call was last updated.
   */
  updatedAt: string(),
});

/**
 * Payload sent to customer webhook when a call is fully analyzed.
 * @interface
 * @category Webhook
 */
export type WebhookPayload = Infer<typeof WebhookPayloadStruct>;

export function isWebhookPayload(obj: unknown): obj is WebhookPayload {
  return isStruct(obj, WebhookPayloadStruct, "WebhookPayload");
}
